import React, { useState, useRef, useEffect } from 'react';

const BAR_COUNT = 46;

const DEFAULT_BAR_PATTERN = Array.from({ length: BAR_COUNT }, (_, index) => {
  const wave = Math.sin(index * 0.55) * 0.55 + Math.cos(index * 0.24) * 0.35;
  return Math.max(8, Math.min(30, Math.round(17 + wave * 9)));
});

function normalizeBars(bars) {
  if (!Array.isArray(bars) || bars.length === 0) {
    return DEFAULT_BAR_PATTERN;
  }

  const numericBars = bars.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0);

  if (!numericBars.length) {
    return DEFAULT_BAR_PATTERN;
  }

  const sampledBars = Array.from({ length: BAR_COUNT }, (_, index) => {
    const position = (index / Math.max(BAR_COUNT - 1, 1)) * Math.max(numericBars.length - 1, 0);
    const leftIndex = Math.floor(position);
    const rightIndex = Math.min(leftIndex + 1, numericBars.length - 1);
    const ratio = position - leftIndex;
    const interpolated = numericBars[leftIndex] + (numericBars[rightIndex] - numericBars[leftIndex]) * ratio;

    return Math.max(8, Math.min(30, Math.round(interpolated)));
  });

  const hasVariation = sampledBars.some((value) => value !== sampledBars[0]);

  return hasVariation ? sampledBars : DEFAULT_BAR_PATTERN;
}

const ListenVoice = ({ preSignedAudio, bars }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const remainingTime = Math.max(duration - currentTime, 0);
  const visualBars = normalizeBars(bars);

  useEffect(() => {
    // Устанавливаем обработчик для получения длительности аудио
    const audioElement = audioRef.current;

    if (audioElement) {
      const handleLoadedMetadata = () => {
        if (audioElement) {
          setDuration(audioElement.duration);
        }
      };

      audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);

      // Если метаданные уже загружены
      if (audioElement.readyState >= 1) {
        setDuration(audioElement.duration);
      }

      return () => {
        if (audioElement) {
          audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        }
      };
    }
  }, [preSignedAudio]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Вычисляем, сколько палочек должно быть закрашено (плавная анимация)
  const getBarColor = (index, currentTime, duration, totalBars) => {
    if (!duration || !isFinite(duration)) return '#085252';
    const progress = (currentTime / duration) * totalBars;
    return index < progress ? '#A1F69E' : '#085252';
  };

  const formatTime = (time) => {
    if (!isFinite(time)) return '0:00';
    const normalizedTime = Math.ceil(time);
    const minutes = Math.floor(normalizedTime / 60);
    const seconds = normalizedTime % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  console.log('🎧 preSignedAudio', preSignedAudio);

  return (
    <div className="w-full h-[64px] border border-[#233636] bg-[#022424] gap-[17px] rounded-[8px] flex items-center justify-center">
      <button
        onClick={togglePlay}
        className="ml-[13px] w-8 h-8 flex items-center justify-center bg-[#A1F69E] rounded-full"
      >
        {isPlaying ? (
          <object
            data="/icons/VoiceStopButton.svg"
            type="image/svg+xml"
            className="w-[44px] h-[44px] pointer-events-none"
            aria-label="Остановить воспроизведение"
          />
        ) : (
          <object
            data="/icons/VoiceStartButton.svg"
            type="image/svg+xml"
            className="w-[44px] h-[44px] pointer-events-none"
            aria-label="Начать воспроизведение"
          />
        )}
      </button>
      <div className="flex-1 min-w-0 h-[30px] overflow-hidden">
        <div
          className="grid h-full items-center gap-[2px]"
          style={{ gridTemplateColumns: `repeat(${visualBars.length}, minmax(0, 1fr))` }}
        >
          {visualBars.map((barHeight, index) => (
            <div
              key={index}
              className="w-full self-center rounded-[13px] transition-colors duration-100 ease-linear"
              style={{
                height: `${barHeight}px`,
                backgroundColor: getBarColor(index, currentTime, duration, visualBars.length),
              }}
            />
          ))}
        </div>
      </div>
      <span className="text-white font-medium mr-[13px]">{formatTime(remainingTime)}</span>
      <audio
        ref={audioRef}
        src={preSignedAudio}
        onEnded={handleAudioEnded}
        onTimeUpdate={handleTimeUpdate}
        className="hidden"
      />
    </div>
  );
};

export default ListenVoice;
